import React, { useEffect, useState } from 'react'
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Avatar,
  Statistic,
  Row,
  Col,
  Table,
  message,
  Spin,
  Alert,
  Typography,
  Tabs,
  Badge,
  Timeline,
} from 'antd'
import {
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  DollarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store'
import { userService } from '@services/userService'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { TabPane } = Tabs

const UserDetailPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userStats, setUserStats] = useState<any>(null)
  const [userOrders, setUserOrders] = useState<any[]>([])
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([])

  useEffect(() => {
    if (id) {
      loadUserDetail()
    }
  }, [id])

  const loadUserDetail = async () => {
    if (!id) return
    
    setLoading(true)
    setError(null)
    
    try {
      // 获取用户详情
      const userData = await userService.getUserDetail(id)
      setUser(userData)
      
      // 获取用户统计信息
      try {
        const stats = await userService.getUserSubscriptionStats(id)
        setUserStats(stats)
      } catch {
        const subs = Array.isArray(userData.subscriptions)
          ? userData.subscriptions
          : []
        const totalSpent = subs.reduce(
          (s: number, x: any) => s + Number(x.totalAmount ?? 0),
          0
        )
        setUserStats({
          totalOrders: subs.length,
          totalSpent,
          activeSubscriptions: subs.filter(
            (x: any) => x.status === 'ACTIVE' || x.status === 'active'
          ).length,
          totalSubscriptions: subs.length,
        })
      }

      const subs = Array.isArray(userData.subscriptions)
        ? userData.subscriptions
        : []
      setUserSubscriptions(subs)
      setUserOrders(
        subs.map((s: any) => ({
          id: s.id,
          gardenName: s.garden?.name ?? '-',
          status: s.status,
          amount: Number(s.totalAmount ?? 0),
          startDate: s.subscriptionDate,
          endDate: s.expiryDate,
        }))
      )
      
    } catch (error: any) {
      setError(error.message || '获取用户详情失败')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (newStatus: 'active' | 'inactive' | 'banned') => {
    if (!id) return
    
    try {
      await userService.updateUserStatus(id, newStatus)
      message.success('用户状态更新成功')
      loadUserDetail() // 重新加载数据
    } catch (error: any) {
      message.error(error.message || '状态更新失败')
    }
  }

  const normalizeUserUiStatus = (status: string | undefined) =>
    (status || '').toLowerCase()

  const getStatusTag = (status: string) => {
    const key = normalizeUserUiStatus(status)
    const statusMap: Record<string, { color: string; text: string }> = {
      active: { color: 'green', text: '正常' },
      inactive: { color: 'orange', text: '未激活' },
      banned: { color: 'red', text: '已封禁' },
    }
    const config = statusMap[key] || { color: 'default', text: status || '-' }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  const getSubscriptionStatusTag = (status: string) => {
    const key = normalizeUserUiStatus(status)
    const statusMap: Record<string, { color: string; text: string }> = {
      active: { color: 'green', text: '进行中' },
      expired: { color: 'orange', text: '已过期' },
      cancelled: { color: 'red', text: '已取消' },
    }
    const config = statusMap[key] || { color: 'default', text: status || '-' }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  const getGenderText = (gender: number) => {
    return gender === 1 ? '男' : gender === 2 ? '女' : '未知'
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={loadUserDetail}>
            重新加载
          </Button>
        }
      />
    )
  }

  if (!user) {
    return (
      <Alert
        message="用户不存在"
        description="找不到指定的用户信息"
        type="warning"
        showIcon
      />
    )
  }

  return (
    <div>
      {/* 页面头部 */}
      <div style={{ marginBottom: '24px' }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/users')}
          >
            返回用户列表
          </Button>
        </Space>
        <Title level={2} style={{ margin: '16px 0 8px 0' }}>
          用户详情
        </Title>
        <Text type="secondary">
          查看和管理用户详细信息
        </Text>
      </div>

      {/* 用户基本信息 */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Avatar 
                size={80} 
                src={user.avatar || user.avatarUrl}
                icon={<UserOutlined />}
              />
              <div style={{ marginTop: '16px' }}>
                <Title level={4} style={{ margin: 0 }}>
                  {user.nickname || user.username || '未设置昵称'}
                </Title>
                <Text type="secondary">{user.level || '普通用户'}</Text>
              </div>
            </div>
          </Col>
          <Col span={18}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="用户ID">
                <Text code>{user.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="用户名">
                {user.username}
              </Descriptions.Item>
              <Descriptions.Item label="昵称">
                {user.nickname || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="手机号">
                <Space>
                  <PhoneOutlined />
                  {user.phone || '未绑定'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="性别">
                <Tag color={user.gender === 1 ? 'blue' : 'pink'}>
                  {getGenderText(user.gender)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                {getStatusTag(user.status)}
              </Descriptions.Item>
              <Descriptions.Item label="地区" span={2}>
                <Space>
                  <EnvironmentOutlined />
                  {(() => {
                    const addr = user.addresses?.find(a => a.isDefault) || user.addresses?.[0]
                    return addr ? `${addr.province || ''} ${addr.city || ''} ${addr.district || ''}` : '未设置'
                  })()}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="详细地址" span={2}>
                {(() => {
                  const addr = user.addresses?.find(a => a.isDefault) || user.addresses?.[0]
                  return addr ? addr.detail : '未设置'
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="注册时间">
                <Space>
                  <CalendarOutlined />
                  {dayjs(user.createdAt).format('YYYY-MM-DD HH:mm')}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="最后登录">
                {user.lastLoginAt ? 
                  dayjs(user.lastLoginAt).format('YYYY-MM-DD HH:mm') : 
                  '从未登录'
                }
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* 统计信息 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总订单数"
              value={userStats?.totalOrders || user.totalOrders || 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总消费"
              value={userStats?.totalSpent || user.totalSpent || 0}
              prefix={<DollarOutlined />}
              formatter={(val) => `¥ ${val}`}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="活跃订阅"
              value={userStats?.activeSubscriptions || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总订阅数"
              value={userStats?.totalSubscriptions || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 详细信息标签页 */}
      <Card>
        <Tabs defaultActiveKey="orders">
          <TabPane tab="订单记录" key="orders">
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              业务以订阅为履约单位，下列为订阅生成的记录。
            </Text>
            <Table
              rowKey="id"
              dataSource={userOrders}
              pagination={false}
              locale={{ emptyText: '暂无记录' }}
              columns={[
                { title: '订阅ID', dataIndex: 'id', key: 'id', ellipsis: true },
                { title: '菜园', dataIndex: 'gardenName', key: 'gardenName' },
                {
                  title: '金额',
                  dataIndex: 'amount',
                  key: 'amount',
                  render: (v: number) => `¥${v}`,
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  key: 'status',
                  render: (s: string) => getSubscriptionStatusTag(s),
                },
                {
                  title: '开始',
                  dataIndex: 'startDate',
                  key: 'startDate',
                  render: (t: string) => dayjs(t).format('YYYY-MM-DD'),
                },
                {
                  title: '结束',
                  dataIndex: 'endDate',
                  key: 'endDate',
                  render: (t: string) => dayjs(t).format('YYYY-MM-DD'),
                },
              ]}
            />
          </TabPane>
          <TabPane tab="订阅记录" key="subscriptions">
            <Table
              rowKey="id"
              dataSource={userSubscriptions}
              pagination={false}
              locale={{ emptyText: '暂无订阅记录' }}
              columns={[
                { title: '订阅ID', dataIndex: 'id', key: 'id', ellipsis: true },
                {
                  title: '菜园',
                  key: 'garden',
                  render: (_: unknown, r: any) => r.garden?.name ?? '-',
                },
                {
                  title: '套餐',
                  dataIndex: 'planType',
                  key: 'planType',
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  key: 'status',
                  render: (s: string) => getSubscriptionStatusTag(s),
                },
                {
                  title: '金额',
                  key: 'totalAmount',
                  render: (_: unknown, r: any) =>
                    `¥${Number(r.totalAmount ?? 0)}`,
                },
                {
                  title: '订阅日',
                  dataIndex: 'subscriptionDate',
                  key: 'subscriptionDate',
                  render: (t: string) => dayjs(t).format('YYYY-MM-DD'),
                },
                {
                  title: '到期',
                  dataIndex: 'expiryDate',
                  key: 'expiryDate',
                  render: (t: string) => dayjs(t).format('YYYY-MM-DD'),
                },
              ]}
            />
          </TabPane>
          <TabPane tab="操作记录" key="actions">
            <Timeline>
              <Timeline.Item 
                dot={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                color="green"
              >
                <Text strong>用户注册</Text>
                <div style={{ color: '#999', fontSize: '12px' }}>
                  {dayjs(user.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                </div>
              </Timeline.Item>
              {user.lastLoginAt && (
                <Timeline.Item 
                  dot={<UserOutlined style={{ color: '#1890ff' }} />}
                  color="blue"
                >
                  <Text strong>最后登录</Text>
                  <div style={{ color: '#999', fontSize: '12px' }}>
                    {dayjs(user.lastLoginAt).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                </Timeline.Item>
              )}
            </Timeline>
          </TabPane>
        </Tabs>
      </Card>

      {/* 操作按钮 */}
      <Card style={{ marginTop: '24px' }}>
        <Space>
          <Button icon={<EditOutlined />}>
            编辑用户信息
          </Button>
          {(user.status === 'active' || user.status === 'ACTIVE') && (
            <Button 
              icon={<CloseCircleOutlined />} 
              danger
              onClick={() => handleStatusUpdate('banned')}
            >
              封禁用户
            </Button>
          )}
          {(user.status === 'banned' || user.status === 'BANNED') && (
            <Button 
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusUpdate('active')}
            >
              解封用户
            </Button>
          )}
          {(user.status === 'inactive' || user.status === 'INACTIVE') && (
            <Button 
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusUpdate('active')}
            >
              激活用户
            </Button>
          )}
        </Space>
      </Card>
    </div>
  )
}

export default UserDetailPage





